import type { Card, Check, DeckLine, FormatName, SavedLine, UnknownLine, Zone } from "./api";

// The deck editor's edits to a deck's lines, made here first so the page answers at once, then
// saved whole; and what the page shows of them. The rules a deck is judged by are the Worker's
// (server/deck_format), which every save answers with: nothing here judges a deck.

export const COLORS = ["W", "U", "B", "R", "G"] as const;
export type Color = (typeof COLORS)[number];

export const TYPE_GROUPS = ["Creature", "Planeswalker", "Battle", "Instant", "Sorcery", "Artifact", "Enchantment", "Land"] as const;
export type TypeGroup = (typeof TYPE_GROUPS)[number];

export const GROUP_LABEL: Record<TypeGroup, string> = {
  Creature: "Creatures", Planeswalker: "Planeswalkers", Battle: "Battles", Instant: "Instants",
  Sorcery: "Sorceries", Artifact: "Artifacts", Enchantment: "Enchantments", Land: "Lands",
};

// A card is grouped by its first type in this order, so an artifact creature is a creature.
export function typeGroup(card: Card): TypeGroup {
  const types = card.type_line.split(" — ")[0]!.split(/\s+/);
  if (types.includes("Land")) return "Land";
  return TYPE_GROUPS.find((t) => types.includes(t)) ?? "Artifact";
}

export const inZone = (lines: DeckLine[], zone: Zone): DeckLine[] => lines.filter((l) => l.zone === zone);

export const countIn = (lines: DeckLine[], zone: Zone): number => inZone(lines, zone).reduce((n, l) => n + l.count, 0);

// What the format counts toward its size: Commander counts the commanders.
export function deckSize(format: FormatName, lines: DeckLine[]): number {
  return countIn(lines, "main") + (format === "Commander" ? countIn(lines, "commander") : 0);
}

export const sizeTarget = (format: FormatName): string => (format === "Commander" ? "100" : "60+");

export const sizeKept = (format: FormatName, size: number): boolean => (format === "Commander" ? size === 100 : size >= 60);

export const byName = (a: DeckLine, b: DeckLine): number => a.card.name.localeCompare(b.card.name);

export const isLand = (card: Card): boolean => typeGroup(card) === "Land";

// Nonland main-deck cards by mana value, 7 and up together.
export function manaCurve(lines: DeckLine[]): number[] {
  const curve = Array.from({ length: 8 }, () => 0);
  for (const l of inZone(lines, "main")) {
    if (!isLand(l.card)) curve[Math.min(7, Math.floor(l.card.mana_value))]! += l.count;
  }
  return curve;
}

// The Art view's columns: nonland main-deck cards by mana value, then the lands.
export function artColumns(lines: DeckLine[]): { label: string; lines: DeckLine[] }[] {
  const main = inZone(lines, "main").sort(byName);
  const columns = Array.from({ length: 8 }, (_, mv) => ({
    label: mv === 7 ? "7+" : String(mv),
    lines: main.filter((l) => !isLand(l.card) && Math.min(7, Math.floor(l.card.mana_value)) === mv),
  }));
  return [...columns, { label: "Lands", lines: main.filter((l) => isLand(l.card)) }].filter((c) => c.lines.length > 0);
}

// The commanders' combined color identity, in WUBRG order.
export function commanderIdentity(lines: DeckLine[]): Color[] {
  const colors = new Set(inZone(lines, "commander").flatMap((l) => l.card.color_identity));
  return COLORS.filter((c) => colors.has(c));
}

// Whether the card may lead a deck: one that can be a commander, or a Background beside one.
export const canLead = (card: Card): boolean => card.can_be_commander || (card.can_pair && /\bBackground\b/.test(card.type_line));

export function withCard(lines: DeckLine[], card: Card, zone: Zone, delta: number): DeckLine[] {
  const i = lines.findIndex((l) => l.card.oracle_id === card.oracle_id && l.zone === zone);
  if (i < 0) return delta > 0 ? [...lines, { card, zone, count: delta }] : lines;
  const count = lines[i]!.count + delta;
  return count <= 0 ? lines.filter((_, j) => j !== i) : lines.map((l, j) => (j === i ? { ...l, count } : l));
}

// `card` becomes a commander, taken from the main deck if it's there. A second commander joins only
// when both can pair; otherwise it replaces the commander, who goes back to the main deck.
export function withCommander(lines: DeckLine[], card: Card): DeckLine[] {
  const fromMain = inZone(lines, "main").some((l) => l.card.oracle_id === card.oracle_id);
  let next = fromMain ? withCard(lines, card, "main", -1) : lines;
  const current = inZone(next, "commander").map((l) => l.card);
  const joins = current.length === 1 && current[0]!.can_pair && card.can_pair;
  if (!joins) {
    for (const c of current) next = withCard(withCard(next, c, "commander", -1), c, "main", 1);
  }
  return withCard(next, card, "commander", 1);
}

export const withoutCommander = (lines: DeckLine[], card: Card): DeckLine[] =>
  withCard(withCard(lines, card, "commander", -1), card, "main", 1);

// Every line, the unknown ones too, as a save sends them.
export function savedLines(lines: DeckLine[], unknown: UnknownLine[]): SavedLine[] {
  return [
    ...lines.map((l) => ({ oracle_id: l.card.oracle_id, count: l.count, zone: l.zone })),
    ...unknown.map((l) => ({ oracle_id: l.oracle_id, count: l.count, zone: l.zone })),
  ];
}

export const copiesIn = (lines: DeckLine[], oracleId: string): number =>
  lines.filter((l) => l.card.oracle_id === oracleId).reduce((n, l) => n + l.count, 0);

// What a broken rule says about a card: the playable rule, which every room enforces, or a format rule.
export type CardFlag = "unplayable" | "rule";

export function flagsByName(checks: Check[]): Map<string, CardFlag> {
  const flags = new Map<string, CardFlag>();
  for (const check of checks) {
    if (check.problems.length === 0) continue;
    for (const name of check.cards) {
      if (check.always_enforced) flags.set(name, "unplayable");
      else if (!flags.has(name)) flags.set(name, "rule");
    }
  }
  return flags;
}

export type RoomRules = "Casual" | "Strict";

export type Verdict = { level: "ok" | "warn" | "blocked"; headline: string };

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

// How a room of `rules` takes the deck: being playable on this release is always enforced; a
// Casual room warns about the format's rules and plays the deck, a Strict room refuses it.
export function verdict(checks: Check[], unknownLines: UnknownLine[], rules: RoomRules, release: string): Verdict {
  const unplayable = checks.filter((c) => c.always_enforced && c.problems.length > 0);
  const broken = checks.filter((c) => !c.always_enforced && c.problems.length > 0);
  if (unplayable.length > 0) {
    const cards = unplayable.reduce((n, c) => n + c.cards.length, 0) + unknownLines.length;
    return { level: "blocked", headline: `Can't be picked on ${release}: ${plural(cards, "card")} this release can't play` };
  }
  if (broken.length > 0 && rules === "Strict") {
    return { level: "blocked", headline: `A Strict room refuses it: ${plural(broken.length, "rule")} broken` };
  }
  if (broken.length > 0) return { level: "warn", headline: `A Casual room plays it, with ${plural(broken.length, "warning")}` };
  return { level: "ok", headline: "Ready for Casual and Strict rooms" };
}

// The release this page belongs to, from its /r/<n>/ path: "r3"; "this release" off a release's path.
export function releaseName(path: string): string {
  const n = /\/r\/(\d+)\//.exec(path)?.[1];
  return n === undefined ? "this release" : `r${n}`;
}

// The symbols of a mana cost: "{2}{R}{W}" is ["2", "R", "W"].
export const costSymbols = (cost: string): string[] => [...cost.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]!);

// The color a symbol's pip takes: a hybrid's first color, C for colorless, "" for generic.
export function pipColor(symbol: string): string {
  return symbol.split("/").find((s) => (COLORS as readonly string[]).includes(s)) ?? (symbol === "C" ? "C" : "");
}
